// TYPES
export type ISODate = string | undefined;

export const TYPE_DATE: DateType = 'date';
export const TYPE_TIME: DateType = 'time';

export type locales = 'it' | 'it-IT';
export type DateType = 'date' | 'time';

export type formats =
  | 'D'
  | 'D MMM'
  | 'D MMMM'
  | 'DD MMM YYYY hh:mm'
  | 'DD/MM/YYYY'
  | 'ddd D MMM'
  | 'dddd D MMMM'
  | 'dddd D MMM YYYY'
  | 'dddd D MMMM YYYY'
  | 'dddd D MMMM YYYY hh:mm'
  | 'hh:mm'
  | 'HH:mm'
  | 'hh:mm:ss'
  | 'MMMM';

// INTERFACES
export interface IDateFormat {
  date?: string;
  locale?: locales;
  format?: formats;
  timeZone?: string;
}

export interface IDateConfigMap {
  [key: string]: IDateConfigObject;
}

export interface IDateConfigObject {
  options: Intl.DateTimeFormatOptions;
  type: DateType;
  transform?: (str: string) => string;
}

interface IOpt {
  readonly weekday: optTypes;
  readonly year: optNumeric;
  readonly month: optTypes;
  readonly day: optNumeric;
  readonly timeZone: string;
}

type optTypes = 'long' | 'short' | 'narrow' | undefined;
type optNumeric = 'numeric' | '2-digit' | undefined;


export const getDateConfigMap = (): IDateConfigMap => {
  return {
    /** DATE TYPES */
    'DD/MM/YYYY': {
      options: { year: 'numeric', month: '2-digit', day: '2-digit' },
      type: TYPE_DATE,
    },
    'ddd D MMM': {
      options: { weekday: 'short', month: 'short', day: 'numeric' },
      type: TYPE_DATE,
    },
    'dddd D MMMM YYYY': {
      options: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' },
      type: TYPE_DATE,
    },
    'dddd D MMMM YYYY hh:mm': {
      options: {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      },
      type: TYPE_DATE,
    },
    'dddd D MMMM': {
      options: { weekday: 'long', month: 'long', day: 'numeric' },
      type: TYPE_DATE,
    },
    'dddd D MMM YYYY': {
      options: { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' },
      type: TYPE_DATE,
    },
    'DD MMM YYYY hh:mm': {
      options: { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: 'numeric' },
      type: TYPE_DATE,
    },
    'D MMMM': { options: { day: 'numeric', month: 'long' }, type: TYPE_DATE },
    'D MMM': { options: { day: 'numeric', month: 'short' }, type: TYPE_DATE },
    D: { options: { day: 'numeric' }, type: TYPE_DATE },
    MMMM: {
      options: { month: 'long' },
      type: TYPE_DATE,
    },

    /** TIME TYPES */
    'hh:mm:ss': { options: { hour: '2-digit', minute: '2-digit', second: '2-digit' }, type: TYPE_TIME },
    'hh:mm': { options: { hour: '2-digit', minute: '2-digit' }, type: TYPE_TIME },
    'HH:mm': { options: { hour: '2-digit', minute: '2-digit' }, type: TYPE_TIME },
  };
};

export const dateFormat = ({
  date,
  locale = 'it-IT',
  format = 'dddd D MMMM YYYY',
  timeZone = 'Europe/Rome',
}: IDateFormat): string => {
  const dateObject = date ? new Date(date) : new Date();
  let dateElm;
  const { options, type, transform } = getDateConfigMap()[format];
  if (type === TYPE_TIME) {
    dateElm = dateObject?.toLocaleTimeString(locale, { ...options, timeZone });
  } else if (type === TYPE_DATE) {
    dateElm = dateObject?.toLocaleDateString(locale, { ...options, timeZone });
  } else {
    /* istanbul ignore next: unreachable code */ return date || '';
  }
  return transform && dateElm ? transform(dateElm) : dateElm;
};

const isValidDate = (date: Date): boolean => date instanceof Date && !isNaN(date.valueOf());

export const ISODateStringToDate = (isoDateString: ISODate): Date | undefined => {
  try {
    const isoDate = isoDateString ? new Date(isoDateString) : undefined;
    // console.log({ isoDateString, isoDate });
    return isoDate ? (isValidDate(isoDate) ? isoDate : undefined) : undefined; // eslint-disable-line no-nested-ternary
  } catch {
    return undefined;
  }
};

export const ISODateStringToTimestamp = (isoDateString: ISODate): number | undefined => {
  const isoDate = ISODateStringToDate(isoDateString);
  return isoDate ? (isValidDate(isoDate) ? isoDate.getTime() : undefined) : undefined; // eslint-disable-line no-nested-ternary
};


export const fromDateToSeconds = (dateISOString: string) =>
  new Date(dateISOString).setTime(new Date(dateISOString).getTime()) / 1000;

export const getTimezoneTime = (date: Date, locale: locales = 'it-IT', timeZone = 'Europe/Rome'): string => {
  return new Date(date).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone });
};

export const getMatchDateTimeString = (date: Date, locale: locales = 'it-IT', timeZone = 'Europe/Rome'): string => {
  const options: IOpt = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone };
  return new Date(date).toLocaleDateString(locale, options);
};
